<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { required, email } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { splitName } from '@chatwoot/utils';
import countries from 'shared/constants/countries.js';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useAccount } from 'dashboard/composables/useAccount';
import { useStore } from 'dashboard/composables/store';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import CompanySelector from 'dashboard/components-next/Companies/CompanySelector.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import PhoneNumberInput from 'dashboard/components-next/phonenumberinput/PhoneNumberInput.vue';
import { campaignsAPI } from 'dashboard/api/jabvox/campaigns';

const props = defineProps({
  contactData: {
    type: Object,
    default: null,
  },
  isDetailsView: {
    type: Boolean,
    default: false,
  },
  isNewContact: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update']);

const { t } = useI18n();
const { currentAccount, isCloudFeatureEnabled } = useAccount();
const store = useStore();

const currentCampaignName = ref('');

const leadsModuleEnabled = ref(false);
const campaigns = ref([]);

const loadCampaigns = async () => {
  try {
    const { data } = await campaignsAPI.getAll();
    campaigns.value = data;
    leadsModuleEnabled.value = true;
  } catch {
    // feature not enabled or unavailable
  }
};

// Se lanza al crear el componente, no en onMounted, porque el watch de abajo es
// `immediate` y necesita saber si el módulo está activo antes de pedir nada.
const campaignsLoaded = loadCampaigns();

// Campaña actual del contacto, para preseleccionarla en el desplegable.
//
// Se toma de lo que YA trae el contacto; no se consulta al servidor. Este formulario se
// monta una vez por tarjeta de la lista, y también con objetos que no son contactos —el
// remitente de una conversación puede ser el agente—, así que pedir el detalle hacía una
// petición por tarjeta y llenaba la consola de 404. Si el dato no viene, el desplegable
// queda vacío hasta abrir la ficha completa, que sí carga el detalle.
const loadLeadCampaign = async () => {
  await campaignsLoaded;
  if (!leadsModuleEnabled.value) return;

  currentCampaignName.value = props.contactData?.campaign?.name || '';
};

const FORM_CONFIG = {
  FIRST_NAME: { field: 'firstName' },
  LAST_NAME: { field: 'lastName' },
  EMAIL_ADDRESS: { field: 'email' },
  PHONE_NUMBER: { field: 'phoneNumber' },
  CITY: { field: 'additionalAttributes.city' },
  COUNTRY: { field: 'additionalAttributes.countryCode' },
  BIO: { field: 'additionalAttributes.description' },
  COMPANY_NAME: { field: 'additionalAttributes.companyName' },
};

const SOCIAL_CONFIG = {
  LINKEDIN: 'i-ri-linkedin-box-fill',
  FACEBOOK: 'i-ri-facebook-circle-fill',
  INSTAGRAM: 'i-ri-instagram-line',
  TELEGRAM: 'i-ri-telegram-fill',
  TIKTOK: 'i-ri-tiktok-fill',
  TWITTER: 'i-ri-twitter-x-fill',
  GITHUB: 'i-ri-github-fill',
};

const defaultState = {
  id: 0,
  name: '',
  email: '',
  companyId: '',
  firstName: '',
  lastName: '',
  phoneNumber: '',
  additionalAttributes: {
    description: '',
    companyName: '',
    countryCode: '',
    country: '',
    city: '',
    jabvoxContactType: '',
    jabvoxIdentificationNumber: '',
    socialProfiles: {
      facebook: '',
      github: '',
      instagram: '',
      telegram: '',
      tiktok: '',
      linkedin: '',
      twitter: '',
    },
  },
};

const state = reactive({ ...defaultState });

const onCampaignBlur = async () => {
  const contactId = state.id;
  if (!contactId) return;
  try {
    await store.dispatch('contacts/update', {
      id: contactId,
      jabvox_campaign_name: currentCampaignName.value || '',
    });
  } catch {
    // silently ignore
  }
};

const validationRules = {
  firstName: { required },
  email: { email },
};

const v$ = useVuelidate(validationRules, state);

const isFormInvalid = computed(() => v$.value.$invalid);
const hasCompaniesFeature = computed(
  () =>
    currentAccount.value?.id && isCloudFeatureEnabled(FEATURE_FLAGS.COMPANIES)
);
const showCompanySelector = computed(
  () =>
    hasCompaniesFeature.value &&
    (Boolean(state.companyId) || !state.additionalAttributes.companyName)
);

const emitContactUpdate = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  const { firstName, lastName, ...stateWithoutNames } = state;
  emit('update', stateWithoutNames);
};

const cm = v => (v === '***' ? '' : (v ?? ''));

const prepareStateBasedOnProps = () => {
  if (props.isNewContact) {
    return; // Added to prevent state update for new contact form
  }

  const {
    id,
    name = '',
    email: emailAddress,
    phoneNumber,
    companyId = '',
    additionalAttributes = {},
  } = props.contactData || {};
  const { firstName, lastName } = splitName(name || '');
  const {
    description = '',
    companyName = '',
    countryCode = '',
    country = '',
    city = '',
    jabvox_contact_type: jabvoxContactType = '',
    jabvox_identification_number: jabvoxIdentificationNumber = '',
    socialTelegramUserName = '',
    socialProfiles = {},
  } = additionalAttributes || {};

  const telegramUsername =
    socialProfiles?.telegram || socialTelegramUserName || '';

  const cleanedSocialProfiles = Object.fromEntries(
    Object.entries({ ...socialProfiles }).map(([k, v]) => [k, cm(v)])
  );

  Object.assign(state, {
    id,
    name: cm(name),
    companyId: companyId || '',
    firstName: cm(firstName),
    lastName: cm(lastName),
    email: cm(emailAddress),
    phoneNumber: cm(phoneNumber),
    additionalAttributes: {
      description: cm(description),
      companyName: cm(companyName),
      countryCode: cm(countryCode),
      country: cm(country),
      city: cm(city),
      jabvoxContactType: cm(jabvoxContactType),
      jabvoxIdentificationNumber: cm(jabvoxIdentificationNumber),
      socialProfiles: {
        ...cleanedSocialProfiles,
        telegram: cm(telegramUsername),
      },
    },
  });
};

const countryOptions = computed(() =>
  countries.map(({ name, id }) => ({ label: name, value: id }))
);

const editDetailsForm = computed(() =>
  Object.keys(FORM_CONFIG).map(key => ({
    key,
    placeholder: t(
      `CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.${key}.PLACEHOLDER`
    ),
  }))
);

const socialProfilesForm = computed(() =>
  Object.entries(SOCIAL_CONFIG).map(([key, icon]) => ({
    key,
    placeholder: t(`CONTACTS_LAYOUT.CARD.SOCIAL_MEDIA.FORM.${key}.PLACEHOLDER`),
    icon,
  }))
);

const isValidationField = key => {
  const field = FORM_CONFIG[key]?.field;
  return ['firstName', 'email'].includes(field);
};

const getValidationKey = key => {
  return FORM_CONFIG[key]?.field;
};

// Creates a computed property for two-way form field binding
const getFormBinding = key => {
  const field = FORM_CONFIG[key]?.field;
  if (!field) return null;

  return computed({
    get: () => {
      // Handle firstName/lastName fields
      if (field === 'firstName' || field === 'lastName') {
        return state[field]?.toString() || '';
      }

      // Handle nested vs non-nested fields
      const [base, nested] = field.split('.');
      // Example: 'email' → state.email
      // Example: 'additionalAttributes.city' → state.additionalAttributes.city
      return (nested ? state[base][nested] : state[base])?.toString() || '';
    },

    set: async value => {
      // Handle name fields specially to maintain the combined 'name' field
      if (field === 'firstName' || field === 'lastName') {
        state[field] = value;
        // Example: firstName="John", lastName="Doe" → name="John Doe"
        state.name = `${state.firstName} ${state.lastName}`.trim();
      } else {
        // Handle nested vs non-nested fields
        const [base, nested] = field.split('.');
        if (nested) {
          // Example: additionalAttributes.city = "New York"
          state[base][nested] = value;
        } else {
          // Example: email = "test@example.com"
          state[base] = value;
        }
      }

      await emitContactUpdate();
    },
  });
};

const getMessageType = key => {
  return isValidationField(key) && v$.value[getValidationKey(key)]?.$error
    ? 'error'
    : 'info';
};

const handleCountrySelection = value => {
  const selectedCountry = countries.find(option => option.id === value);
  state.additionalAttributes.country = selectedCountry?.name || '';
  emit('update', state);
};

const handleCompanySelection = async ({ id, name }) => {
  state.companyId = id || '';
  state.additionalAttributes.companyName = name || '';
  await emitContactUpdate();
};

const resetValidation = () => {
  v$.value.$reset();
};

const resetForm = () => {
  Object.assign(state, defaultState);
};

watch(
  () => props.contactData?.id,
  id => {
    if (id) {
      prepareStateBasedOnProps();
      loadLeadCampaign();
    }
  },
  { immediate: true }
);

// Expose state to parent component for avatar upload
defineExpose({
  state,
  currentCampaignName,
  leadsModuleEnabled,
  campaigns,
  resetValidation,
  isFormInvalid,
  resetForm,
});
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="flex flex-col items-start gap-2">
      <span class="py-1 text-sm font-medium text-n-slate-12">
        {{ t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.TITLE') }}
      </span>
      <div class="grid w-full grid-cols-1 gap-4 sm:grid-cols-2">
        <template v-for="item in editDetailsForm" :key="item.key">
          <ComboBox
            v-if="item.key === 'COUNTRY'"
            v-model="state.additionalAttributes.countryCode"
            :options="countryOptions"
            :placeholder="item.placeholder"
            class="[&>div>button]:h-8"
            :class="{
              '[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:!outline-transparent':
                !isDetailsView,
              '[&>div>button]:!bg-n-alpha-black2': isDetailsView,
            }"
            @update:model-value="handleCountrySelection"
          />
          <PhoneNumberInput
            v-else-if="item.key === 'PHONE_NUMBER'"
            v-model="getFormBinding(item.key).value"
            :placeholder="item.placeholder"
            :show-border="isDetailsView"
          />
          <CompanySelector
            v-else-if="item.key === 'COMPANY_NAME' && showCompanySelector"
            :model-value="state.companyId"
            :selected-name="state.additionalAttributes.companyName"
            :is-details-view="isDetailsView"
            @select="handleCompanySelection"
          />
          <Input
            v-else
            v-model="getFormBinding(item.key).value"
            :placeholder="item.placeholder"
            :message-type="getMessageType(item.key)"
            :custom-input-class="`h-8 !pt-1 !pb-1 ${
              !isDetailsView
                ? '[&:not(.error,.focus)]:!outline-transparent'
                : ''
            }`"
            class="w-full"
            @input="
              isValidationField(item.key) &&
                v$[getValidationKey(item.key)].$touch()
            "
            @blur="
              isValidationField(item.key) &&
                v$[getValidationKey(item.key)].$touch()
            "
          />
        </template>
      </div>
    </div>
    <!-- Jabvox: contact type + identification -->
    <div class="flex flex-col items-start gap-3">
      <span class="py-1 text-sm font-medium text-n-slate-12">
        {{ t('CONTACT_FORM.FORM.JABVOX_CONTACT_TYPE.LABEL') }}
      </span>
      <div class="flex items-center gap-4">
        <label
          class="flex items-center gap-1.5 cursor-pointer text-sm text-n-slate-12"
        >
          <input
            v-model="state.additionalAttributes.jabvoxContactType"
            type="radio"
            value="persona"
            class="accent-n-brand"
            @change="emit('update', state)"
          />
          {{ t('CONTACT_FORM.FORM.JABVOX_CONTACT_TYPE.PERSONA') }}
        </label>
        <label
          class="flex items-center gap-1.5 cursor-pointer text-sm text-n-slate-12"
        >
          <input
            v-model="state.additionalAttributes.jabvoxContactType"
            type="radio"
            value="empresa"
            class="accent-n-brand"
            @change="emit('update', state)"
          />
          {{ t('CONTACT_FORM.FORM.JABVOX_CONTACT_TYPE.EMPRESA') }}
        </label>
      </div>
      <Input
        v-if="state.additionalAttributes.jabvoxContactType"
        v-model="state.additionalAttributes.jabvoxIdentificationNumber"
        :placeholder="
          state.additionalAttributes.jabvoxContactType === 'empresa'
            ? t('CONTACT_FORM.FORM.JABVOX_IDENTIFICATION.NIT_PLACEHOLDER')
            : t('CONTACT_FORM.FORM.JABVOX_IDENTIFICATION.CEDULA_PLACEHOLDER')
        "
        :custom-input-class="`h-8 !pt-1 !pb-1 ${!isDetailsView ? '[&:not(.error,.focus)]:!outline-transparent' : ''}`"
        class="w-full"
        @input="emit('update', state)"
      />
    </div>

    <!-- Jabvox: campaign -->
    <div v-if="leadsModuleEnabled" class="flex flex-col items-start gap-2">
      <span class="py-1 text-sm font-medium text-n-slate-12">
        {{ t('CONTACT_FORM.FORM.JABVOX_CAMPAIGN.LABEL') }}
      </span>
      <select
        v-model="currentCampaignName"
        class="w-full h-8 rounded-lg border border-n-weak bg-n-alpha-2 dark:bg-n-solid-2 text-sm text-n-slate-12 pl-3 pr-7 focus:outline-none focus:ring-1 focus:ring-n-brand"
        @change="onCampaignBlur"
      >
        <option value="">
          {{ t('CONTACT_FORM.FORM.JABVOX_CAMPAIGN.NONE') }}
        </option>
        <option v-for="c in campaigns" :key="c.id" :value="c.name">
          {{ c.name }}
        </option>
      </select>
    </div>

    <!-- Hueco para lo que deba ir entre los datos y las redes sociales — la ficha unificada
         mete aquí los campos personalizados de la cuenta. Las redes quedan siempre al final
         por ser lo que menos se consulta al gestionar un contacto. -->
    <slot name="before-social" />

    <div class="flex flex-col items-start gap-2">
      <span class="py-1 text-sm font-medium text-n-slate-12">
        {{ t('CONTACTS_LAYOUT.CARD.SOCIAL_MEDIA.TITLE') }}
      </span>
      <div class="flex flex-wrap gap-2">
        <div
          v-for="item in socialProfilesForm"
          :key="item.key"
          class="flex items-center h-8 gap-2 px-2 rounded-lg"
          :class="{
            'bg-n-alpha-2 dark:bg-n-solid-2': isDetailsView,
            'bg-n-alpha-2 dark:bg-n-solid-3': !isDetailsView,
          }"
        >
          <Icon
            :icon="item.icon"
            class="flex-shrink-0 text-n-slate-11 size-4"
          />
          <input
            v-model="
              state.additionalAttributes.socialProfiles[item.key.toLowerCase()]
            "
            class="w-auto min-w-[100px] text-sm bg-transparent outline-none reset-base text-n-slate-12 dark:text-n-slate-12 placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10"
            :placeholder="item.placeholder"
            :size="item.placeholder.length"
            @input="emit('update', state)"
          />
        </div>
      </div>
    </div>
  </div>
</template>
